import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, XCircle, Clock, Coins, RefreshCw } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "",          label: "All statuses" },
  { value: "complete",  label: "complete" },
  { value: "initiated", label: "initiated" },
  { value: "expired",   label: "expired" },
];
const PAY_OPTIONS = [
  { value: "",       label: "All payments" },
  { value: "paid",   label: "paid" },
  { value: "unpaid", label: "unpaid" },
  { value: "failed", label: "failed" },
];

const KPI = ({ label, value, accent, Icon, testid }) => (
  <div
    data-testid={testid}
    className="bg-[#0F1525] border border-[#1E2A3F] rounded-md p-5"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{label}</div>
      <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ background: accent + "22", color: accent }}>
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
    </div>
    <div className="font-display text-3xl tracking-tight" style={{ fontWeight: 800, color: accent }}>
      {value}
    </div>
  </div>
);

const PaymentBadge = ({ status }) => {
  const map = {
    paid:   { c: "#00FF94", bg: "rgba(0,255,148,0.12)" },
    unpaid: { c: "#94A3B8", bg: "rgba(148,163,184,0.12)" },
    failed: { c: "#EF4444", bg: "rgba(239,68,68,0.15)" },
  };
  const m = map[status] || map.unpaid;
  return (
    <span
      className="inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] border rounded-sm"
      style={{ color: m.c, borderColor: m.c, background: m.bg }}
    >
      {status || "—"}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    complete:  { c: "#3B82F6" },
    initiated: { c: "#F59E0B" },
    expired:   { c: "#64748B" },
  };
  const m = map[status] || { c: "#64748B" };
  return (
    <span
      className="inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] border rounded-sm"
      style={{ color: m.c, borderColor: m.c, background: m.c + "1F" }}
    >
      {status || "—"}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (payFilter)    params.payment_status = payFilter;
      const { data } = await api.get("/admin/payments", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, payFilter]);

  if (loading && !data) {
    return <div className="p-10 font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">// Loading payments…</div>;
  }

  const stats = data?.stats || {};
  const paid = stats.by_payment_status?.paid || 0;
  const unpaid = stats.by_payment_status?.unpaid || 0;
  const failed = stats.by_payment_status?.failed || 0;

  return (
    <div className="p-8 lg:p-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">// PAYMENTS</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight" style={{ fontWeight: 800 }}>
            Payment transactions
          </h1>
          <p className="text-sm text-[#94A3B8] mt-2">
            Stripe Checkout sessions plus free-mode audit entries.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="payments-refresh-btn"
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#1E2A3F] hover:border-[#3B82F6] hover:text-[#3B82F6] font-mono text-[10px] uppercase tracking-[0.2em]"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPI testid="payments-kpi-revenue" label="Revenue (¥)" value={`¥${(stats.revenue_paid_jpy || 0).toLocaleString()}`} accent="#F59E0B" Icon={Coins} />
        <KPI testid="payments-kpi-paid"    label="Paid"        value={paid}   accent="#00FF94" Icon={CheckCircle2} />
        <KPI testid="payments-kpi-unpaid"  label="Unpaid"      value={unpaid} accent="#94A3B8" Icon={Clock} />
        <KPI testid="payments-kpi-failed"  label="Failed"      value={failed} accent="#EF4444" Icon={XCircle} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="payments-filter-status"
            className="bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none px-3 py-1.5 text-xs text-white font-mono"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">Payment:</span>
          <select
            value={payFilter}
            onChange={(e) => setPayFilter(e.target.value)}
            data-testid="payments-filter-payment"
            className="bg-[#0A0E1A] border border-[#1E2A3F] focus:border-[#3B82F6] outline-none px-3 py-1.5 text-xs text-white font-mono"
          >
            {PAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] ml-auto">
          {data?.total ?? 0} of latest 200
        </div>
      </div>

      <div className="bg-[#0F1525] border border-[#1E2A3F] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="border-b border-[#1E2A3F]">
              <tr className="text-[#64748B] font-mono text-[10px] uppercase tracking-[0.2em]">
                <th className="text-left p-3">Created</th>
                <th className="text-left p-3">Session</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Payment</th>
                <th className="text-left p-3">Result</th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Mode</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((t) => {
                const isFree = (t.metadata?.mode === "free") || (t.session_id || "").startsWith("free-");
                return (
                  <tr
                    key={t.session_id || t.id}
                    data-testid={`payment-row-${t.session_id || t.id}`}
                    className="border-b border-[#1E2A3F]/60 hover:bg-[#3B82F6]/5"
                  >
                    <td className="p-3 text-[#94A3B8] whitespace-nowrap font-mono text-xs">{fmtDate(t.created_at)}</td>
                    <td className="p-3 font-mono text-[11px] text-white truncate max-w-[180px]" title={t.session_id}>
                      {t.session_id?.slice(0, 22)}{t.session_id && t.session_id.length > 22 ? "…" : ""}
                    </td>
                    <td className="p-3 text-right font-mono text-white">
                      {(t.amount ?? 0).toLocaleString()} {(t.currency || "").toUpperCase()}
                    </td>
                    <td className="p-3"><StatusBadge status={t.status} /></td>
                    <td className="p-3"><PaymentBadge status={t.payment_status} /></td>
                    <td className="p-3 font-mono text-[11px] text-[#94A3B8] truncate max-w-[140px]" title={t.result_id}>
                      {t.result_id?.slice(0, 8) || "—"}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-[#94A3B8] truncate max-w-[140px]" title={t.user_id || ""}>
                      {t.user_id ? t.user_id.slice(0, 8) : "anon"}
                    </td>
                    <td className="p-3">
                      {isFree ? (
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#00FF94]">free</span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#3B82F6]">stripe</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(!data?.items || data.items.length === 0) && (
                <tr>
                  <td colSpan={8} className="p-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748B]">
                    No transactions yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
